import React, { useState } from 'react';
import { useAcademyStore } from '../../../store/academyStore';
import { CheckSquare, Square, Backpack } from 'lucide-react';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Mission Briefing",
        icon: "🎒",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Welcome, Explorer!</h3>
                    <p className="text-slate-700 mb-4">
                        You are about to embark on a journey through the Digital Jungle.
                        Before we start, we need to make sure your <strong>Backpack</strong> is packed.
                    </p>
                    <p className="text-slate-700">
                        In this quick check, confirm you have the tools and permission needed to learn.
                    </p>
                </div>
            </div>
        )
    }
];

// ============ ACTIVITY COMPONENT ============
const ChecklistGame = ({ onComplete }: { onComplete: () => void }) => {
    const [checks, setChecks] = useState<Record<string, boolean>>({});

    const items = [
        { id: 'google-account', text: 'A Google Account (Ask an adult for help!)' },
        { id: 'browser', text: 'A modern web browser (Chrome, Edge, or Firefox)' },
        { id: 'notebook', text: 'A physical notebook for sketching ideas' },
        { id: 'curiosity', text: 'A curious mind ready to explore!' },
        { id: 'permission', text: 'Permission from a parent or guardian to start' },
    ];

    const toggleCheck = (id: string) => {
        setChecks(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const allChecked = items.every(i => checks[i.id]);

    const handleChecklistComplete = () => {
        if (allChecked) {
            onComplete();
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                <Backpack className="text-blue-600" /> Your Inventory
            </h2>

            <div className="space-y-4 mb-8">
                {items.map(item => (
                    <div
                        key={item.id}
                        onClick={() => toggleCheck(item.id)}
                        className={`
                            p-4 rounded-xl border-2 flex items-center gap-4 cursor-pointer transition-all
                            ${checks[item.id] ? 'bg-green-50 border-green-200 shadow-sm' : 'bg-white border-slate-200 hover:border-blue-300'}
                        `}
                    >
                        <div className={`
                            w-8 h-8 rounded-lg flex items-center justify-center border-2 transition-colors
                            ${checks[item.id] ? 'bg-green-500 border-green-600 text-white' : 'bg-slate-100 border-slate-300 text-slate-300'}
                        `}>
                            {checks[item.id] ? <CheckSquare size={20} /> : <Square size={20} />}
                        </div>
                        <span className={`text-lg font-bold ${checks[item.id] ? 'text-green-800' : 'text-slate-500'}`}>
                            {item.text}
                        </span>
                    </div>
                ))}
            </div>

            <button
                onClick={handleChecklistComplete}
                disabled={!allChecked}
                className={`
                    w-full py-4 rounded-xl font-black text-xl flex items-center justify-center gap-2 transition-all
                    ${allChecked ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-green-200/50' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
                `}
            >
                {allChecked ? 'I am Ready! →' : 'Check all items to continue'}
            </button>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const Prerequisites = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('prerequisites', 50);
    };

    return (
        <LessonWrapper
            moduleName="Backpack Check"
            moduleEmoji="🎒"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Checklist"
        >
            <ChecklistGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default Prerequisites;
